import { SeparatorList } from "@narmi/design_system";
import { useLocalization } from "@fluent/react";
import Account from "byzantine/src/Account";
import { useNotificationContext } from "cerulean";

import { useAccountsContext } from "../../../components/contexts/AccountContext";
import { ExternalAccountAction } from "../dashboard/ExternalAccounts/ExternalAccountRow/ExternalAccountRow";

import styles from "./AccountsModule.module.scss";

import type { AccountClassType } from "byzantine/src/Account";
import type { AddAccountMode } from "../../../components/settings/linked_accounts/types";

const formatProductType = (productType: string): string =>
  productType.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export const getAccountSubtitleParts = (
  account: AccountClassType
): string[] => {
  if (account.isInternal()) return [account.getMaskedNumber()];
  return [
    account.fi_name,
    account.product_type
      ? formatProductType(account.product_type as string)
      : "",
  ].filter(Boolean);
};

export const useAccountActions = (account: AccountClassType) => {
  const { updateAccount, accountUpdateInFlightRef } = useAccountsContext();
  const { sendNotification } = useNotificationContext();
  const { l10n } = useLocalization();

  const handleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    accountUpdateInFlightRef.current = true;
    account
      .toggleFavorited()
      .then((response) => updateAccount(Account.deserialize(response.account)))
      .catch(() =>
        sendNotification({
          type: "negative",
          text: l10n.getString("accounts-favorite-error"),
        })
      )
      .finally(() => {
        accountUpdateInFlightRef.current = false;
      });
  };

  const handleToggleHidden = (e: React.MouseEvent) => {
    e.stopPropagation();
    account
      .toggleHidden()
      .then((response) => updateAccount(Account.deserialize(response.account)))
      .catch(() =>
        sendNotification({
          type: "negative",
          text: l10n.getString("accounts-visibility-error"),
        })
      );
  };

  return { handleFavorite, handleToggleHidden };
};

interface AccountCardProps {
  account: AccountClassType;
  isEditing?: boolean;
  isHidden?: boolean;
  onVerify?: (account: AccountClassType) => void;
  onTriggerInstantVerification?: (mode: AddAccountMode) => void;
}

const AccountCard = ({
  account,
  isEditing = false,
  isHidden = false,
  onVerify,
  onTriggerInstantVerification,
}: AccountCardProps) => {
  const { l10n } = useLocalization();
  const { handleFavorite, handleToggleHidden } = useAccountActions(account);
  const name = account.getName();
  const subtitleParts = getAccountSubtitleParts(account);
  const balance = account.displayBalance();
  const isInternal = account.isInternal();
  const isClickable = isInternal && !isEditing;

  const navigateToAccount = () => {
    if (isClickable) window.location.assign(`/accounts/${account.id}`);
  };

  let actionButton = null;
  if (isEditing) {
    actionButton = (
      <button
        type="button"
        className={`${styles.actionButton} ${styles.actionButtonVisible}`}
        onClick={handleToggleHidden}
        aria-label={
          isHidden
            ? l10n.getString("accounts-unhide-account")
            : l10n.getString("accounts-hide-account")
        }
      >
        <span className={isHidden ? "narmi-icon-eye-off" : "narmi-icon-eye"} />
      </button>
    );
  } else if (isInternal) {
    actionButton = (
      <button
        type="button"
        className={`${styles.starButton} ${
          account.favorited ? styles.starButtonActive : ""
        }`}
        onClick={handleFavorite}
        // Necessary to prevent `onKeyDown` navigation behavior in the parent
        onKeyDown={(e) => e.stopPropagation()}
        aria-label={
          account.favorited
            ? l10n.getString("accounts-unfavorite-account")
            : l10n.getString("accounts-favorite-account")
        }
      >
        <span
          className={
            account.favorited ? "narmi-icon-star-filled" : "narmi-icon-star"
          }
        />
      </button>
    );
  }

  return (
    <div
      className={`${styles.card} ${
        !isClickable ? styles.cardNotClickable : ""
      }`}
      onClick={navigateToAccount}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={(e) => {
        if (isClickable && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          navigateToAccount();
        }
      }}
    >
      <div className={styles.cardHeader}>
        <span className={styles.cardName}>{name}</span>
        {actionButton}
      </div>
      {subtitleParts.length > 0 && (
        <div className={styles.cardNumber}>
          <SeparatorList items={subtitleParts} separator="•" />
        </div>
      )}
      <div className={styles.cardFooter}>
        {isInternal || !onVerify || !onTriggerInstantVerification ? (
          <span className={styles.cardBalance}>{balance}</span>
        ) : (
          <ExternalAccountAction
            account={account}
            onVerify={onVerify}
            onTriggerInstantVerification={onTriggerInstantVerification}
            balanceVariant="card"
          />
        )}
      </div>
    </div>
  );
};

export default AccountCard;
